import { IAction, reducer } from "store";
import { DealAction } from "store/actions";
import { DealFolderForm, PublishDealFormAnswerSockets } from "store/types";
import { filterObject } from "utils/comments";

import { initialState } from "../../store-context";
import { DealFormAnswer, DealFormData, DealFormQuestion, IUser, UpdateDealFormQuestionOrder } from "../../types";

// For a new branched structure
reducer.createHandler(DealAction.GetFormsSuccess, (state, { payload }: IAction<Record<number, DealFolderForm>>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    forms: payload
  }
}));

// For a new branched structure
reducer.createHandler(DealAction.CreateFormSuccess, (state, { payload }: IAction<DealFolderForm>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    forms: { ...state.dealModule.forms, [payload.id]: payload }
  }
}));

// For a new branched structure
reducer.createHandler(DealAction.UpdateFormSuccess, (state, { payload }: IAction<DealFolderForm>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    forms: { ...state.dealModule.forms, [payload.id]: payload },
    ...(payload.id === state.dealModule.form.data?.id && {
      form: {
        ...state.dealModule.form,
        data: {
          ...state.dealModule.form.data,
          ...payload
        }
      }
    })
  }
}));

// For a new branched structure
reducer.createHandler(DealAction.DeleteFormSuccess, (state, { payload }: IAction<number>) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    forms: filterObject([payload], state.dealModule.forms)
  }
}));

reducer.createHandler(DealAction.GetDealFormRequest, (state) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    form: {
      ...state.dealModule.form,
      loading: true
    }
  }
}));

reducer.createHandler(DealAction.GetDealFormSuccess, (state, { payload }: IAction<DealFormData>) => {
  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: payload,
        loading: false
      }
    }
  };
});

reducer.createHandler(DealAction.GetDealFormFailure, (state) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    form: initialState.dealModule.form
  }
}));

export const updateQuestion = reducer.createHandler(
  DealAction.UpdateDealFormQuestion,
  (state, { payload }: IAction<DealFormQuestion>) => {
    const { deal_form_group_id, uuid, deal_form_id } = payload;

    const { data } = state.dealModule.form;

    if (!data) return state;
    if (deal_form_id !== data.id) return state;

    return {
      ...state,
      dealModule: {
        ...state.dealModule,
        form: {
          ...state.dealModule.form,
          data: {
            ...data,
            groups: {
              ...data.groups,
              [deal_form_group_id]: {
                ...data.groups[deal_form_group_id],
                questions: {
                  ...data.groups[deal_form_group_id].questions,
                  [uuid]: {
                    ...data.groups[deal_form_group_id].questions[uuid],
                    ...payload
                  }
                }
              }
            }
          }
        }
      }
    };
  }
);

export const updateQuestionOrder = reducer.createHandler(
  DealAction.UpdateDealFormQuestionOrder,
  (state, { payload }: IAction<UpdateDealFormQuestionOrder>) => {
    const { groupId, questionOne, questionTwo } = payload;
    const { deal_form_id } = questionOne;

    const { data } = state.dealModule.form;

    if (!data) return state;
    if (deal_form_id !== data.id) return state;

    return {
      ...state,
      dealModule: {
        ...state.dealModule,
        form: {
          ...state.dealModule.form,
          data: {
            ...data,
            groups: {
              ...data.groups,
              [groupId]: {
                ...data.groups[groupId],
                questions: {
                  ...data.groups[groupId].questions,
                  [questionOne.uuid]: {
                    ...data.groups[groupId].questions[questionOne.uuid],
                    ...questionOne
                  },
                  [questionTwo.uuid]: {
                    ...data.groups[groupId].questions[questionTwo.uuid],
                    ...questionTwo
                  }
                }
              }
            }
          }
        }
      }
    };
  }
);

reducer.createHandler(DealAction.ClearDealForm, (state) => ({
  ...state,
  dealModule: {
    ...state.dealModule,
    form: initialState.dealModule.form
  }
}));

reducer.createHandler(DealAction.PublishDealFormAnswerSuccess, (state, { payload }: IAction<DealFormAnswer>) => {
  const { deal_form_id } = payload;
  const { data } = state.dealModule.form;

  if (!data) return state;
  if (deal_form_id !== data.id) return state;
  if (!data?.groups[payload.question_group_id]?.questions[payload.question_uuid]) return state;

  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: {
          ...data,
          groups: {
            ...data.groups,
            [payload.question_group_id]: {
              ...data.groups[payload.question_group_id],
              questions: {
                ...data.groups[payload.question_group_id].questions,
                [payload.question_uuid]: {
                  ...data.groups[payload.question_group_id].questions[payload.question_uuid],
                  value: payload,
                  value_last_updated: {
                    date: payload.published_at,
                    user_id: payload.user_id
                  },
                  draft: null
                }
              }
            }
          }
        }
      }
    }
  };
});

reducer.createHandler(
  DealAction.PublishDealFormAnswerSocket,
  (state, { payload }: IAction<PublishDealFormAnswerSockets>) => {
    const { answer } = payload;
    if (!answer) return state;

    if (!("question_group_id" in answer) || !("question_uuid" in answer)) return state;

    const { deal_form_id } = answer;
    const { data } = state.dealModule.form;

    if (!data) return state;
    if (deal_form_id !== data.id) return state;
    if (!data?.groups[answer.question_group_id]?.questions[answer.question_uuid]) return state;

    return {
      ...state,
      dealModule: {
        ...state.dealModule,
        form: {
          ...state.dealModule.form,
          data: {
            ...data,
            groups: {
              ...data.groups,
              [answer.question_group_id]: {
                ...data.groups[answer.question_group_id],
                questions: {
                  ...data.groups[answer.question_group_id].questions,
                  [answer.question_uuid]: {
                    ...data.groups[answer.question_group_id].questions[answer.question_uuid],
                    value: answer,
                    value_last_updated: {
                      date: answer.published_at,
                      user_id: answer.user_id
                    },
                    draft: null
                  }
                }
              }
            }
          }
        }
      }
    };
  }
);

reducer.createHandler(DealAction.ClearDealFormAnswerSuccess, (state, { payload }: IAction<DealFormAnswer>) => {
  const { deal_form_id } = payload;
  const { data } = state.dealModule.form;

  if (!data) return state;
  if (deal_form_id !== data.id) return state;
  if (!data?.groups[payload.question_group_id]?.questions[payload.question_uuid]) return state;

  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: {
          ...data,
          groups: {
            ...data.groups,
            [payload.question_group_id]: {
              ...data.groups[payload.question_group_id],
              questions: {
                ...data.groups[payload.question_group_id].questions,
                [payload.question_uuid]: {
                  ...data.groups[payload.question_group_id].questions[payload.question_uuid],
                  value: null,
                  value_last_updated: null
                }
              }
            }
          }
        }
      }
    }
  };
});

reducer.createHandler(DealAction.SetDealFormDraft, (state, { payload }: IAction<DealFormAnswer>) => {
  const { deal_form_id } = payload;
  const { data } = state.dealModule.form;

  if (!data) return state;
  if (deal_form_id !== data.id) return state;
  if (!data?.groups[payload.question_group_id]?.questions[payload.question_uuid]) return state;

  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: {
          ...data,
          groups: {
            ...data.groups,
            [payload.question_group_id]: {
              ...data.groups[payload.question_group_id],
              questions: {
                ...data.groups[payload.question_group_id].questions,
                [payload.question_uuid]: {
                  ...data.groups[payload.question_group_id].questions[payload.question_uuid],
                  draft: payload
                }
              }
            }
          }
        }
      }
    }
  };
});

reducer.createHandler(DealAction.RequestRefreshDealFormSocket, (state, { payload }: IAction<boolean>) => {
  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        request_refresh: payload
      }
    }
  };
});

reducer.createHandler(DealAction.GetDealFormAnswerSuccess, (state, { payload }: IAction<DealFormAnswer>) => {
  const { deal_form_id } = payload;
  const { data } = state.dealModule.form;

  if (!data) return state;
  if (deal_form_id !== data.id) return state;
  if (!data?.groups[payload.question_group_id]?.questions[payload.question_uuid]) return state;

  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: {
          ...data,
          groups: {
            ...data.groups,
            [payload.question_group_id]: {
              ...data.groups[payload.question_group_id],
              questions: {
                ...data.groups[payload.question_group_id].questions,
                [payload.question_uuid]: {
                  ...data.groups[payload.question_group_id].questions[payload.question_uuid],
                  value: payload
                }
              }
            }
          }
        }
      }
    }
  };
});

reducer.createHandler(DealAction.DiscardDealFormDraft, (state, { payload }: IAction<DealFormAnswer>) => {
  const { deal_form_id } = payload;
  const { data } = state.dealModule.form;

  if (!data) return state;
  if (deal_form_id !== data.id) return state;
  if (!data?.groups[payload.question_group_id]?.questions[payload.question_uuid]) return state;

  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: {
          ...data,
          groups: {
            ...data.groups,
            [payload.question_group_id]: {
              ...data.groups[payload.question_group_id],
              questions: {
                ...data.groups[payload.question_group_id].questions,
                [payload.question_uuid]: {
                  ...data.groups[payload.question_group_id].questions[payload.question_uuid],
                  draft: null
                }
              }
            }
          }
        }
      }
    }
  };
});

reducer.createHandler(DealAction.AddUserDealForm, (state, { payload }: IAction<IUser>) => {
  const { data } = state.dealModule.form;

  if (!data) return state;

  return {
    ...state,
    dealModule: {
      ...state.dealModule,
      form: {
        ...state.dealModule.form,
        data: {
          ...data,
          users: { ...data.users, [payload.id]: payload }
        }
      }
    }
  };
});
